import { FC, useMemo } from 'react';
import { Grid, Button, Box, Typography, Card, CardContent, useTheme } from '@mui/material';
import { ArrowBack } from '@mui/icons-material';
import PaginatedGrid from '../../../../components/paginatedGrid/PaginatedGrid';
import {
  useStudentAssessmentState,
  useStudentAssessmentActions,
} from '../../contexts/studentAssessment.context';
import {
  getStudentAssessmentDetailsGridColumns,
  getScoreListGridColumns,
} from './studentAssessments.helpers';
import { useTranslation } from 'src/hooks/useTranslationHook';
import { AddButton, EditButton } from 'src/views/components/simple/AllButtons';
import { PermissionGate } from '../../../../../components/rbac/PermissionGate';
import { useStudentFiltersState } from '../../contexts/studentFilters.context';
import { GRADE } from '../../../../../apis/admissions/admissions.interfaces';
import { IScoreRecord } from '../../../../../apis/score/score.interfaces';

/** Dev/local preview rows when Grade 1 is selected in student filters (score list view). */
const GRADE_1_MOCK_SCORE_RECORDS: IScoreRecord[] = [
  {
    id: 'mock-g1-score-1',
    studentId: 'mock-s1',
    studentName: 'Aarav Sharma',
    classId: 'mock-class-g1',
    className: 'Grade 1',
    section: 'A',
    academicYear: '2025-26',
    marks: 82,
    maxMarks: 100,
    percentage: 82,
    overallGrade: 'A',
  },
  {
    id: 'mock-g1-score-2',
    studentId: 'mock-s2',
    studentName: 'Diya Patel',
    classId: 'mock-class-g1',
    className: 'Grade 1',
    section: 'A',
    academicYear: '2025-26',
    marks: 71,
    maxMarks: 100,
    percentage: 71,
    overallGrade: 'B',
  },
  {
    id: 'mock-g1-score-3',
    studentId: 'mock-s3',
    studentName: 'Kabir Singh',
    classId: 'mock-class-g1',
    className: 'Grade 1',
    section: 'B',
    academicYear: '2025-26',
    marks: 94,
    maxMarks: 100,
    percentage: 94,
    overallGrade: 'A+',
  },
  {
    id: 'mock-g1-score-4',
    studentId: 'mock-s4',
    studentName: 'Ishaan Verma',
    classId: 'mock-class-g1',
    className: 'Grade 1',
    section: 'A',
    academicYear: '2025-26',
    marks: 88,
    maxMarks: 100,
    percentage: 88,
    overallGrade: 'A',
  },
  {
    id: 'mock-g1-score-5',
    studentId: 'mock-s5',
    studentName: 'Myra Iyer',
    classId: 'mock-class-g1',
    className: 'Grade 1',
    section: 'A',
    academicYear: '2025-26',
    marks: 65,
    maxMarks: 100,
    percentage: 65,
    overallGrade: 'C',
  },
  {
    id: 'mock-g1-score-6',
    studentId: 'mock-s6',
    studentName: 'Vihaan Reddy',
    classId: 'mock-class-g1',
    className: 'Grade 1',
    section: 'B',
    academicYear: '2025-26',
    marks: 79,
    maxMarks: 100,
    percentage: 79,
    overallGrade: 'B',
  },
  {
    id: 'mock-g1-score-7',
    studentId: 'mock-s7',
    studentName: 'Anaya Krishnan',
    classId: 'mock-class-g1',
    className: 'Grade 1',
    section: 'B',
    academicYear: '2025-26',
    marks: 91,
    maxMarks: 100,
    percentage: 91,
    overallGrade: 'A',
  },
  {
    id: 'mock-g1-score-8',
    studentId: 'mock-s8',
    studentName: 'Reyansh Nair',
    classId: 'mock-class-g1',
    className: 'Grade 1',
    section: 'C',
    academicYear: '2025-26',
    marks: 58,
    maxMarks: 100,
    percentage: 58,
    overallGrade: 'C',
  },
  {
    id: 'mock-g1-score-9',
    studentId: 'mock-s9',
    studentName: 'Kavya Menon',
    classId: 'mock-class-g1',
    className: 'Grade 1',
    section: 'C',
    academicYear: '2025-26',
    marks: 76,
    maxMarks: 100,
    percentage: 76,
    overallGrade: 'B',
  },
  {
    id: 'mock-g1-score-10',
    studentId: 'mock-s10',
    studentName: 'Arjun Desai',
    classId: 'mock-class-g1',
    className: 'Grade 1',
    section: 'A',
    academicYear: '2025-26',
    marks: 85,
    maxMarks: 100,
    percentage: 85,
    overallGrade: 'A',
  },
  {
    id: 'mock-g1-score-11',
    studentId: 'mock-s11',
    studentName: 'Pari Choudhary',
    classId: 'mock-class-g1',
    className: 'Grade 1',
    section: 'B',
    academicYear: '2025-26',
    marks: 69,
    maxMarks: 100,
    percentage: 69,
    overallGrade: 'C+',
  },
  {
    id: 'mock-g1-score-12',
    studentId: 'mock-s12',
    studentName: 'Yuvan Bose',
    classId: 'mock-class-g1',
    className: 'Grade 1',
    section: 'C',
    academicYear: '2025-26',
    marks: 97,
    maxMarks: 100,
    percentage: 97,
    overallGrade: 'A+',
  },
  {
    id: 'mock-g1-score-13',
    studentId: 'mock-s13',
    studentName: 'Zara Khan',
    classId: 'mock-class-g1',
    className: 'Grade 1',
    section: 'C',
    academicYear: '2025-26',
    marks: 73,
    maxMarks: 100,
    percentage: 73,
    overallGrade: 'B',
  },
];

const StudentAssessmentsGrid: FC = () => {
  const { translateText } = useTranslation();
  const theme = useTheme();
  const filters = useStudentFiltersState();
  const {
    subjectBasedAssessmentList,
    scoreRecordList,
    studentAssessmentDetails,
    studentAssessmentAppState: { selectedAssessmentId, isLoading },
  } = useStudentAssessmentState();
  const { setSelectedAssessmentId, openModal } = useStudentAssessmentActions();

  const scoreListRows = useMemo(() => {
    if (filters.grade === GRADE.ONE) {
      return GRADE_1_MOCK_SCORE_RECORDS;
    }
    return scoreRecordList;
  }, [filters.grade, scoreRecordList]);

  const scoreListColumns = useMemo(
    () => getScoreListGridColumns(translateText),
    [translateText]
  );

  const studentDetailsColumns = useMemo(
    () => getStudentAssessmentDetailsGridColumns(translateText),
    [translateText]
  );

  const handleRowClick = (params: any) => {
    const assessment = subjectBasedAssessmentList.find(
      (a) => a.id === params.row.id
    );
    if (assessment) {
      setSelectedAssessmentId(assessment.id);
    }
  };

  const selectedAssessment = useMemo(() => {
    if (!selectedAssessmentId) return null;
    return (
      subjectBasedAssessmentList.find((a) => a.id === selectedAssessmentId) ||
      null
    );
  }, [selectedAssessmentId, subjectBasedAssessmentList]);

  const onAddAssessmentClickHandler = () => {
    openModal();
  };

  const onEditAssessmentClickHandler = () => {
    if (studentAssessmentDetails) {
      openModal(studentAssessmentDetails);
    }
  };

  const handleBackToAssessments = () => {
    setSelectedAssessmentId('');
  };

  return (
    <Grid
      container
      spacing={0}
      sx={{ height: '100%' }}
      data-testid="student-assessments-grid"
    >
      <Grid
        item
        xs={12}
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: selectedAssessment ? 'space-between' : 'end',
          mb: { xs: 1, sm: 2 },
          flexDirection: { xs: 'column', sm: 'row' },
          gap: { xs: 1.5, sm: 0 },
        }}
        data-testid="student-assessments-header"
      >
        {selectedAssessment ? (
          <Box
            display="flex"
            alignItems="center"
            sx={{
              flexDirection: { xs: 'column', sm: 'row' },
              gap: { xs: 1, sm: 0 },
              width: { xs: '100%', sm: 'auto' },
            }}
          >
            <Button
              startIcon={<ArrowBack />}
              onClick={handleBackToAssessments}
              sx={{
                mr: { xs: 0, sm: 2 },
                minHeight: { xs: '44px', sm: '36px' },
                fontSize: { xs: '16px', sm: '14px' },
                width: { xs: '100%', sm: 'auto' },
              }}
              data-testid="back-to-assessments-button"
            >
              {translateText('students.assessments.backToAssessments')}
            </Button>
            <Typography
              variant="h6"
              data-testid="assessment-details-title"
              sx={{
                fontSize: { xs: '1rem', sm: '1.25rem' },
                textAlign: { xs: 'center', sm: 'left' },
                width: { xs: '100%', sm: 'auto' },
              }}
            >
              {translateText('students.assessments.assessmentDetails')}
            </Typography>
          </Box>
        ) : null}
        {selectedAssessment ? (
          <PermissionGate
            module="students"
            submodule="scores"
            permission="write"
          >
            <EditButton
              label={translateText('students.assessments.editAssessment')}
              onClickHandler={onEditAssessmentClickHandler}
              data-testid="edit-assessment-button"
            />
          </PermissionGate>
        ) : (
          <PermissionGate
            module="students"
            submodule="scores"
            permission="write"
          >
            <AddButton
              label={translateText('students.assessments.addAssessment')}
              onClickHandler={onAddAssessmentClickHandler}
              data-testid="add-assessment-button"
            />
          </PermissionGate>
        )}
      </Grid>

      {selectedAssessment && studentAssessmentDetails && (
        <Grid item xs={12} data-testid="assessment-summary-section">
          <Card
            elevation={0}
            sx={{
              mb: 2,
              background: `linear-gradient(135deg, ${theme.palette.primary.light}15 0%, ${theme.palette.primary.light}05 100%)`,
              border: `1px solid ${theme.palette.primary.light}40`,
              borderRadius: 2,
              transition: 'transform 0.2s, box-shadow 0.2s',
              '&:hover': {
                transform: 'translateY(-2px)',
                boxShadow: `0 4px 12px ${theme.palette.primary.light}30`,
              },
            }}
          >
            <CardContent
              sx={{
                p: { xs: 1.5, sm: 2, md: 3 },
                '&:last-child': {
                  pb: { xs: 1.5, sm: 2, md: 3 },
                },
              }}
            >
              <Typography
                variant="h6"
                gutterBottom
                fontWeight={600}
                color={theme.palette.primary.main}
                mb={{ xs: 2, sm: 3 }}
                sx={{
                  fontSize: { xs: '1rem', sm: '1.25rem' },
                }}
              >
                {translateText('students.assessments.summary')}
              </Typography>
              <Grid container spacing={{ xs: 1.5, sm: 2, md: 3 }}>
                <Grid item xs={12}>
                  <Card
                    elevation={0}
                    sx={{
                      background: `linear-gradient(135deg, ${theme.palette.grey[50]} 0%, ${theme.palette.background.paper} 100%)`,
                      border: `1px solid ${theme.palette.divider}`,
                      borderRadius: 2,
                      p: { xs: 1.5, sm: 2 },
                    }}
                  >
                    <Typography
                      variant="caption"
                      color="textSecondary"
                      fontWeight={500}
                      display="block"
                      gutterBottom
                      sx={{
                        fontSize: { xs: '0.75rem', sm: '0.875rem' },
                      }}
                    >
                      {translateText('common.title')}
                    </Typography>
                    <Typography
                      variant="body1"
                      fontWeight={600}
                      sx={{
                        fontSize: { xs: '0.875rem', sm: '1rem' },
                      }}
                    >
                      {studentAssessmentDetails.summary.title || '-'}
                    </Typography>
                  </Card>
                </Grid>
                <Grid item xs={12}>
                  <Card
                    elevation={0}
                    sx={{
                      background: `linear-gradient(135deg, ${theme.palette.grey[50]} 0%, ${theme.palette.background.paper} 100%)`,
                      border: `1px solid ${theme.palette.divider}`,
                      borderRadius: 2,
                      p: { xs: 1.5, sm: 2 },
                    }}
                  >
                    <Typography
                      variant="caption"
                      color="textSecondary"
                      fontWeight={500}
                      display="block"
                      gutterBottom
                      sx={{
                        fontSize: { xs: '0.75rem', sm: '0.875rem' },
                      }}
                    >
                      {translateText('common.description')}
                    </Typography>
                    <Typography
                      variant="body1"
                      sx={{
                        fontSize: { xs: '0.875rem', sm: '1rem' },
                      }}
                    >
                      {studentAssessmentDetails.summary.description || '-'}
                    </Typography>
                  </Card>
                </Grid>
                <Grid item xs={12} sm={6} md={3}>
                  <Card
                    elevation={0}
                    sx={{
                      background: `linear-gradient(135deg, ${theme.palette.info.light}15 0%, ${theme.palette.info.light}05 100%)`,
                      border: `1px solid ${theme.palette.info.light}40`,
                      borderRadius: 2,
                      transition: 'transform 0.2s, box-shadow 0.2s',
                      '&:hover': {
                        transform: 'translateY(-2px)',
                        boxShadow: `0 4px 12px ${theme.palette.info.light}30`,
                      },
                      height: '100%',
                    }}
                  >
                    <CardContent
                      sx={{
                        p: { xs: 1.5, sm: 2 },
                        '&:last-child': {
                          pb: { xs: 1.5, sm: 2 },
                        },
                      }}
                    >
                      <Typography
                        variant="caption"
                        color="textSecondary"
                        fontWeight={500}
                        display="block"
                        gutterBottom
                        sx={{
                          fontSize: { xs: '0.75rem', sm: '0.875rem' },
                        }}
                      >
                        {translateText('students.assessments.totalScore')}
                      </Typography>
                      <Typography
                        variant="h6"
                        fontWeight={600}
                        color={theme.palette.info.main}
                        sx={{
                          fontSize: { xs: '1rem', sm: '1.25rem' },
                        }}
                      >
                        {studentAssessmentDetails.summary.totalScore}
                      </Typography>
                    </CardContent>
                  </Card>
                </Grid>
                <Grid item xs={12} sm={6} md={3}>
                  <Card
                    elevation={0}
                    sx={{
                      background: `linear-gradient(135deg, ${theme.palette.info.light}15 0%, ${theme.palette.info.light}05 100%)`,
                      border: `1px solid ${theme.palette.info.light}40`,
                      borderRadius: 2,
                      transition: 'transform 0.2s, box-shadow 0.2s',
                      '&:hover': {
                        transform: 'translateY(-2px)',
                        boxShadow: `0 4px 12px ${theme.palette.info.light}30`,
                      },
                      height: '100%',
                    }}
                  >
                    <CardContent
                      sx={{
                        p: { xs: 1.5, sm: 2 },
                        '&:last-child': {
                          pb: { xs: 1.5, sm: 2 },
                        },
                      }}
                    >
                      <Typography
                        variant="caption"
                        color="textSecondary"
                        fontWeight={500}
                        display="block"
                        gutterBottom
                        sx={{
                          fontSize: { xs: '0.75rem', sm: '0.875rem' },
                        }}
                      >
                        {translateText('students.assessments.totalPercentage')}
                      </Typography>
                      <Typography
                        variant="h6"
                        fontWeight={600}
                        color={theme.palette.info.main}
                        sx={{
                          fontSize: { xs: '1rem', sm: '1.25rem' },
                        }}
                      >
                        {studentAssessmentDetails.summary.totalPercentage}%
                      </Typography>
                    </CardContent>
                  </Card>
                </Grid>
                <Grid item xs={12} sm={6} md={3}>
                  <Card
                    elevation={0}
                    sx={{
                      background: `linear-gradient(135deg, ${theme.palette.warning.light}15 0%, ${theme.palette.warning.light}05 100%)`,
                      border: `1px solid ${theme.palette.warning.light}40`,
                      borderRadius: 2,
                      transition: 'transform 0.2s, box-shadow 0.2s',
                      '&:hover': {
                        transform: 'translateY(-2px)',
                        boxShadow: `0 4px 12px ${theme.palette.warning.light}30`,
                      },
                      height: '100%',
                    }}
                  >
                    <CardContent
                      sx={{
                        p: { xs: 1.5, sm: 2 },
                        '&:last-child': {
                          pb: { xs: 1.5, sm: 2 },
                        },
                      }}
                    >
                      <Typography
                        variant="caption"
                        color="textSecondary"
                        fontWeight={500}
                        display="block"
                        gutterBottom
                        sx={{
                          fontSize: { xs: '0.75rem', sm: '0.875rem' },
                        }}
                      >
                        {translateText('students.assessments.expectedStudents')}
                      </Typography>
                      <Typography
                        variant="h6"
                        fontWeight={600}
                        color={theme.palette.warning.main}
                        sx={{
                          fontSize: { xs: '1rem', sm: '1.25rem' },
                        }}
                      >
                        {studentAssessmentDetails.summary.expectedStudents}
                      </Typography>
                    </CardContent>
                  </Card>
                </Grid>
                <Grid item xs={12} sm={6} md={3}>
                  <Card
                    elevation={0}
                    sx={{
                      background: `linear-gradient(135deg, ${theme.palette.warning.light}15 0%, ${theme.palette.warning.light}05 100%)`,
                      border: `1px solid ${theme.palette.warning.light}40`,
                      borderRadius: 2,
                      transition: 'transform 0.2s, box-shadow 0.2s',
                      '&:hover': {
                        transform: 'translateY(-2px)',
                        boxShadow: `0 4px 12px ${theme.palette.warning.light}30`,
                      },
                      height: '100%',
                    }}
                  >
                    <CardContent
                      sx={{
                        p: { xs: 1.5, sm: 2 },
                        '&:last-child': {
                          pb: { xs: 1.5, sm: 2 },
                        },
                      }}
                    >
                      <Typography
                        variant="caption"
                        color="textSecondary"
                        fontWeight={500}
                        display="block"
                        gutterBottom
                        sx={{
                          fontSize: { xs: '0.75rem', sm: '0.875rem' },
                        }}
                      >
                        {translateText('students.assessments.actualStudents')}
                      </Typography>
                      <Typography
                        variant="h6"
                        fontWeight={600}
                        color={theme.palette.warning.main}
                        sx={{
                          fontSize: { xs: '1rem', sm: '1.25rem' },
                        }}
                      >
                        {studentAssessmentDetails.summary.actualStudents}
                      </Typography>
                    </CardContent>
                  </Card>
                </Grid>
                <Grid item xs={12} sm={6} md={3}>
                  <Card
                    elevation={0}
                    sx={{
                      background: `linear-gradient(135deg, ${theme.palette.success.light}15 0%, ${theme.palette.success.light}05 100%)`,
                      border: `1px solid ${theme.palette.success.light}40`,
                      borderRadius: 2,
                      transition: 'transform 0.2s, box-shadow 0.2s',
                      '&:hover': {
                        transform: 'translateY(-2px)',
                        boxShadow: `0 4px 12px ${theme.palette.success.light}30`,
                      },
                      height: '100%',
                    }}
                  >
                    <CardContent
                      sx={{
                        p: { xs: 1.5, sm: 2 },
                        '&:last-child': {
                          pb: { xs: 1.5, sm: 2 },
                        },
                      }}
                    >
                      <Typography
                        variant="caption"
                        color="textSecondary"
                        fontWeight={500}
                        display="block"
                        gutterBottom
                        sx={{
                          fontSize: { xs: '0.75rem', sm: '0.875rem' },
                        }}
                      >
                        {translateText('students.assessments.passedStudents')}
                      </Typography>
                      <Typography
                        variant="h6"
                        fontWeight={600}
                        color={theme.palette.success.main}
                        sx={{
                          fontSize: { xs: '1rem', sm: '1.25rem' },
                        }}
                      >
                        {studentAssessmentDetails.summary.passedStudents}
                      </Typography>
                    </CardContent>
                  </Card>
                </Grid>
                <Grid item xs={12} sm={6} md={3}>
                  <Card
                    elevation={0}
                    sx={{
                      background: `linear-gradient(135deg, ${theme.palette.error.light}15 0%, ${theme.palette.error.light}05 100%)`,
                      border: `1px solid ${theme.palette.error.light}40`,
                      borderRadius: 2,
                      transition: 'transform 0.2s, box-shadow 0.2s',
                      '&:hover': {
                        transform: 'translateY(-2px)',
                        boxShadow: `0 4px 12px ${theme.palette.error.light}30`,
                      },
                      height: '100%',
                    }}
                  >
                    <CardContent
                      sx={{
                        p: { xs: 1.5, sm: 2 },
                        '&:last-child': {
                          pb: { xs: 1.5, sm: 2 },
                        },
                      }}
                    >
                      <Typography
                        variant="caption"
                        color="textSecondary"
                        fontWeight={500}
                        display="block"
                        gutterBottom
                        sx={{
                          fontSize: { xs: '0.75rem', sm: '0.875rem' },
                        }}
                      >
                        {translateText('students.assessments.failedStudents')}
                      </Typography>
                      <Typography
                        variant="h6"
                        fontWeight={600}
                        color={theme.palette.error.main}
                        sx={{
                          fontSize: { xs: '1rem', sm: '1.25rem' },
                        }}
                      >
                        {studentAssessmentDetails.summary.failedStudents}
                      </Typography>
                    </CardContent>
                  </Card>
                </Grid>
                <Grid item xs={12} sm={6} md={3}>
                  <Card
                    elevation={0}
                    sx={{
                      background: `linear-gradient(135deg, ${theme.palette.info.light}15 0%, ${theme.palette.info.light}05 100%)`,
                      border: `1px solid ${theme.palette.info.light}40`,
                      borderRadius: 2,
                      transition: 'transform 0.2s, box-shadow 0.2s',
                      '&:hover': {
                        transform: 'translateY(-2px)',
                        boxShadow: `0 4px 12px ${theme.palette.info.light}30`,
                      },
                      height: '100%',
                    }}
                  >
                    <CardContent
                      sx={{
                        p: { xs: 1.5, sm: 2 },
                        '&:last-child': {
                          pb: { xs: 1.5, sm: 2 },
                        },
                      }}
                    >
                      <Typography
                        variant="caption"
                        color="textSecondary"
                        fontWeight={500}
                        display="block"
                        gutterBottom
                        sx={{
                          fontSize: { xs: '0.75rem', sm: '0.875rem' },
                        }}
                      >
                        {translateText('students.assessments.passingScore')}
                      </Typography>
                      <Typography
                        variant="h6"
                        fontWeight={600}
                        color={theme.palette.info.main}
                        sx={{
                          fontSize: { xs: '1rem', sm: '1.25rem' },
                        }}
                      >
                        {studentAssessmentDetails.summary.passingScore}
                      </Typography>
                    </CardContent>
                  </Card>
                </Grid>
              </Grid>
            </CardContent>
          </Card>
        </Grid>
      )}

      <Grid
        item
        xs={12}
        data-testid="student-assessments-grid-container"
        sx={{
          height: 'calc(100vh - 250px)',
          overflow: 'auto',
        }}
      >
        <PaginatedGrid
          dataTestId={
            selectedAssessment
              ? 'student-assessment-details-grid'
              : 'student-score-list-grid'
          }
          rows={
            selectedAssessment && studentAssessmentDetails
              ? studentAssessmentDetails.assessments.map((item) => ({
                id: item.id,
                studentName: `${item.assessment.studentDetails.firstName} ${item.assessment.studentDetails.lastName}`,
                obtainedScore: item.assessment.scores.obtainedScore,
                maxScore: item.assessment.scores.maxScore,
                percentage: item.assessment.scores.percentage,
                date: item.assessment.scores.date,
                remarks: item.assessment.scores.remarks || '-',
              }))
              : scoreListRows.map((record) => ({
                id: record.id,
                studentName: record.studentName,
                className: record.className || '-',
                section: record.section,
                academicYear: record.academicYear,
                marks: record.marks,
                maxMarks: record.maxMarks,
                percentage: record.percentage,
                overallGrade: record.overallGrade,
              }))
          }
          columns={
            selectedAssessment ? studentDetailsColumns : scoreListColumns
          }
          loading={isLoading}
          onRowClick={selectedAssessment ? undefined : handleRowClick}
          getRowId={(row) => row.id}
        />
      </Grid>
    </Grid>
  );
};

export default StudentAssessmentsGrid;
